import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ListItem } from '../../types/list';
import { listClient } from '../../lib/list';
import { useHeader } from '../../context/HeaderContext';
import { CategoryFilter } from '../shared/CategoryFilter';
import { ListGrid } from './ListGrid';
import { DetailCard } from './DetailCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { navigationClient } from '../../lib/navigation';

const ListSection: React.FC = () => {
  // Log component mount
  logger.debug('ListSection component mounting', {
    category: 'Navigation'
  });

  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const section = useSection();
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<ListItem | null>(null);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch items using section.id
  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ['list-items', section?.id],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found for this slug');
      }
      return listClient.getItems(section.id);
    },
    enabled: !!section?.id,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000
  });

  // Log when items query is enabled/disabled
  useEffect(() => {
    logger.debug('Items query status', {
      category: 'API',
      data: {
        enabled: !!section?.id,
        hotel_id: HOTEL_ID,
        section_id: section?.id
      }
    });
  }, [section?.id]);

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analyticsService.trackSectionView(
        section.id,
        'liste',
        'list',
        {
          section_name: section.names.fr || 'Unknown Section'
        }
      );
    }
  }, [section?.id]);

  // Category colors memo
  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, items]);

  // Filtered items memo
  const filteredItems = React.useMemo(() => {
    if (!selectedCategory && !selectedType) return items;
    return items.filter(item => {
      const categoryMatch = !selectedCategory || item.category === selectedCategory;
      const typeMatch = !selectedType || item.type === selectedType;
      return categoryMatch && typeMatch;
    });
  }, [items, selectedCategory, selectedType]);

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items) {
      const item = items.find(i => slugify(i.name) === name);
      if (item) {
        setSelectedItem(item);
      }
    }
  }, [name, items]);

  // Event handlers
  const handleBackClick = () => {
    navigate('/');
  };

  const handleCategorySelect = (category: string) => {
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'category_filter',
      event_data: {
        section_id: section?.id,
        section_name: section?.names?.fr || 'Unknown',
        section_type: 'liste',
        filter_value: category || 'all'
      }
    });
    setSelectedCategory(category);
    setSelectedItem(null);
    
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleTypeSelect = (type: string) => {
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'type_filter',
      event_data: {
        section_id: section?.id,
        section_name: section?.names?.fr || 'Unknown',
        section_type: 'liste',
        filter_value: type || 'all'
      }
    });
    setSelectedType(type);
    setSelectedItem(null);
    
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleItemClick = (item: ListItem) => {
    const itemSlug = slugify(item.name);
    analyticsService.trackItemAction(
      section?.id || '',
      item.id,
      'item_click',
      'liste',
      {
        item_name: item.name,
        category: item.category,
        type: item.type,
        section_name: section?.names?.fr || 'Unknown',
        section_type: 'liste'
      }
    );
    
    // Naviguer vers l'URL de l'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
    
    setSelectedItem(item);
  };

  // Loading state
  if (configLoading || translationsLoading || itemsLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (itemsError || !config || !translations || !section) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
      }}
    >
      <SectionHeader
        config={config}
        title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
        onBackClick={handleBackClick}
        translations={translations}
      />

      {/* Filter */}
      <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
        <CategoryFilter
          selectedCategory={selectedCategory}
          selectedType={selectedType}
          onCategoryChange={handleCategorySelect}
          onTypeChange={handleTypeSelect}
          config={config}
          translations={translations}
          items={items}
        />
      </div>

      {/* Main content */}
      <main className="main-content">
        <ListGrid
          items={filteredItems}
          selectedCategory={selectedCategory}
          onItemClick={handleItemClick}
          onCategorySelect={handleCategorySelect}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
        />
      </main>

      {/* Modals */}
      {selectedItem && (
        <DetailCard
          item={selectedItem}
          items={filteredItems}
          onClose={() => {
            // Naviguer vers la section sans paramètre d'item
            const sectionSlug = slugify(section.names.fr);
            navigate(`/${sectionSlug}`, { replace: true });
            
            setSelectedItem(null);
          }}
          onItemChange={(newItem) => {
            const itemSlug = slugify(newItem.name);
            
            // Naviguer vers l'URL de l'item
            const sectionSlug = slugify(section.names.fr);
            navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
            
            setSelectedItem(newItem);
          }}
          onCategorySelect={handleCategorySelect}
          onTypeSelect={handleTypeSelect}
          config={config}
          translations={translations}
          tableId={section.id}
          categoryColors={categoryColors}
        />
      )}
    </div>
  );
};

export default ListSection;
export { ListSection };